import React from 'react';

import { ISO_MONTH_FORMAT, PathBuilder } from '@gasbi/shared';
import { ErrorView as Error } from '@gasbi/shared';
import { NotFound } from '@gasbi/shared';
import dayjs from 'dayjs';
import {
  Navigate,
  Route,
  RouteObject,
  createBrowserRouter,
} from 'react-router-dom';

import { AppRouteParams } from '@/appRouteParams';
import Pages from '@/pages';
import { organizationRoutes } from '@/pages/MyOrganization/routes';

import AuthCallback from './pages/auth/AuthCallback';

const Router = React.lazy(() => import('@/pages/Router'));
const Home = React.lazy(() => import('@/pages/Home'));
const NotAuthorized = React.lazy(() => import('@/pages/NotAuthorized'));

const PersonalAccounts = React.lazy(() => import('@/pages/PersonalAccounts'));
const PersonalAccountDetails = React.lazy(
  () => import('@/pages/PersonalAccountDetails'),
);
const PersonalAccountDetailsInformation = React.lazy(
  () => import('@/pages/PersonalAccountDetails/views/Information'),
);
const PersonalAccountPaymentHistory = React.lazy(
  () => import('@/pages/PersonalAccountPaymentHistory'),
);

const PersonalAccountDetailsLedger = React.lazy(
  () => import('@/pages/PersonalAccountDetails/views/Ledger'),
);

const UndistributedPayments = React.lazy(
  () => import('@/pages/UndistributedPayments'),
);
const DebtDynamics = React.lazy(
  () => import('@/pages/PersonalAccountDetails/views/DebtDynamics'),
);

const Reports = React.lazy(() => import('@/pages/Reports'));
const ReportsDetails = React.lazy(() => import('@/pages/ReportsDetails'));

const notFoundRoute: RouteObject = {
  path: '*',
  element: <NotFound />,
};

export const routes: RouteObject[] = [
  {
    path: PathBuilder.absolute().page(Pages.ROOT).build(),
    element: <Home />,
  },
  {
    path: PathBuilder.absolute().page(Pages.MY_ORGANIZATION).build(),
    children: organizationRoutes,
  },
  {
    path: PathBuilder.absolute().page(Pages.PERSONAL_ACCOUNTS).build(),
    element: <PersonalAccounts />,
  },
  {
    path: PathBuilder.absolute().page(Pages.UNDISTRIBUTED_PAYMENTS).build(),
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.UNDISTRIBUTED_PAYMENTS)
      .param(AppRouteParams.Date)
      .build(),
    element: <UndistributedPayments />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.PERSONAL_ACCOUNTS)
      .param(AppRouteParams.AccountId)
      .build(),
    element: <PersonalAccountDetails />,
    children: [
      { index: true, element: <PersonalAccountDetailsInformation /> },
      { path: Pages.LEDGER, element: <PersonalAccountDetailsLedger /> },
      { path: Pages.DEBT_DYNAMICS, element: <DebtDynamics /> },
    ],
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.PERSONAL_ACCOUNTS)
      .param(AppRouteParams.AccountId)
      .page(Pages.PAYMENT_HISTORY)
      .build(),
    element: <PersonalAccountPaymentHistory />,
  },
  {
    path: PathBuilder.absolute().page(Pages.REPORTS).build(),
    element: <Navigate to='all' replace />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.REPORTS)
      .param(AppRouteParams.ReportTab)
      .build(),
    element: <Reports />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.REPORTS)
      .page(Pages.BALANCE_FINANCE)
      .build(),
    element: <Navigate to={dayjs().format(ISO_MONTH_FORMAT)} replace />,
  },
  {
    path: PathBuilder.absolute()
      .page(Pages.REPORTS)
      .page(Pages.BALANCE_FINANCE)
      .param(AppRouteParams.Date)
      .build(),
    element: <ReportsDetails />,
  },
  notFoundRoute,
];

export const getNestedRoutes = (routes: RouteObject[]) => {
  return routes.map(({ children, ...route }, index) => (
    // @ts-ignore
    <Route key={index} {...route}>
      {children?.length ? getNestedRoutes(children) : null}
    </Route>
  ));
};

export const getRoutes = () =>
  createBrowserRouter(
    [
      // Cookie (BFF) session: the BFF returns the user to the page they asked
      // for, so only "/" (and the old /callback link) need the landing
      {
        path: Pages.ROOT,
        element: <AuthCallback />,
      },
      {
        path: Pages.AUTH_CALLBACK,
        element: <AuthCallback />,
      },
      {
        path: Pages.NOT_AUTHORIZED,
        element: <NotAuthorized />,
      },
      {
        path: PathBuilder.absolute()
          .param(AppRouteParams.ContextOrganizationId)
          .any()
          .build(),
        element: <Router />,
      },
    ].map(route => ({ ...route, errorElement: <Error /> })),
  );
